import type { Tab } from './types'
import {
  CHEAP_TIP_BELOW,
  GENEROUS_TIP_AT,
  VERY_GENEROUS_TIP_AT,
  formatMoney,
  formatPercent,
  tabPayTotalCents,
  startOfWeek,
  weeksAgo,
} from './money'
import {
  EMPTY_LINES,
  EXTRA_LINES,
  GENEROUS_LINES,
  HABIT_BAR_HEAVY,
  HABIT_CHEAP_PATTERN,
  HABIT_GENEROUS_PATTERN,
  HABIT_HEAVY_SPEND,
  HABIT_HEAVY_WEEK,
  HABIT_QUIET,
  CHEAP_LINES,
  NAVIGATOR_DRIVE_HOME_LINES,
  STANDARD_LINES,
} from './roasts/pools.generated'
import {
  hashRoastLine,
  preferFreshRoastIndices,
  recentRoastHashSet,
  recordSpokenRoast,
} from './roastRecency'

export type RoastTone = 'cheap' | 'standard' | 'generous' | 'extra' | 'habit' | 'navigator'

/** Pre-tip bill at or above this triggers the Navigator / drive-home nudge. */
export const LARGE_TAB_BILL_CENTS = 7500

/** Total out the door at or above this also triggers the nudge. */
export const LARGE_TAB_TOTAL_CENTS = 9000

const HABIT_LINES = {
  heavyWeek: HABIT_HEAVY_WEEK,
  heavySpend: HABIT_HEAVY_SPEND,
  cheapPattern: HABIT_CHEAP_PATTERN,
  generousPattern: HABIT_GENEROUS_PATTERN,
  barHeavy: HABIT_BAR_HEAVY,
  quiet: HABIT_QUIET,
} as const

function hashSeed(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function pickIndex(lines: readonly string[], seed: string, recent: ReadonlySet<string>): number {
  const base = hashSeed(seed) % lines.length
  const all = lines.map((_, i) => i)
  const candidates = preferFreshRoastIndices(lines, all, recent)
  return candidates[hashSeed(`${seed}:pick`) % candidates.length]
}

async function pick(lines: readonly string[], seed: string): Promise<string> {
  const recent = await recentRoastHashSet()
  const idx = pickIndex(lines, seed, recent)
  const text = normalizeRoastLine(lines[idx])
  void recordSpokenRoast(text)
  return text
}

/** Strip legacy generator scaffolding users should never see. */
export function normalizeRoastLine(text: string): string {
  return text.replace(/^Take \d+:\s*/i, '').trim()
}

/** Sync pick for tests and month-summary tail (uses hash only). */
export function pickRoastSync(lines: readonly string[], seed: string): string {
  return normalizeRoastLine(lines[hashSeed(seed) % lines.length])
}

export function tipTone(percent: number): Exclude<RoastTone, 'habit'> {
  if (percent < CHEAP_TIP_BELOW) return 'cheap'
  if (percent >= VERY_GENEROUS_TIP_AT) return 'extra'
  if (percent >= GENEROUS_TIP_AT) return 'generous'
  return 'standard'
}

function linesForTipTone(tone: Exclude<RoastTone, 'habit' | 'navigator'>): readonly string[] {
  if (tone === 'cheap') return CHEAP_LINES
  if (tone === 'extra') return EXTRA_LINES
  if (tone === 'generous') return GENEROUS_LINES
  return STANDARD_LINES
}

export async function roastForTip(percent: number, seed = String(percent)): Promise<string> {
  const tone = tipTone(percent)
  return pick(linesForTipTone(tone), `${tone}:${seed}`)
}

export function roastForTipSync(percent: number, seed = String(percent)): string {
  const tone = tipTone(percent)
  return pickRoastSync(linesForTipTone(tone), `${tone}:${seed}`)
}

export function isLargeTab(billCents: number, tipCents: number, autoGratCents = 0): boolean {
  if (billCents <= 0) return false
  const checkSubtotal = billCents + Math.max(0, autoGratCents)
  return (
    checkSubtotal >= LARGE_TAB_BILL_CENTS ||
    tabPayTotalCents(billCents, tipCents, autoGratCents) >= LARGE_TAB_TOTAL_CENTS
  )
}

export async function roastForLiveBill(
  billCents: number,
  tipCents: number,
  tipPercent: number,
  seed: string,
  autoGratCents = 0,
): Promise<{ text: string; tone: RoastTone }> {
  if (isLargeTab(billCents, tipCents, autoGratCents)) {
    return {
      text: await pick(NAVIGATOR_DRIVE_HOME_LINES, `nav:${billCents}:${tipCents}:${seed}`),
      tone: 'navigator',
    }
  }
  const tone = tipTone(tipPercent)
  return {
    text: await pick(linesForTipTone(tone), `${billCents}:${tipPercent}:${seed}`),
    tone,
  }
}

export async function roastForTab(tab: Tab): Promise<string> {
  const live = await roastForLiveBill(
    tab.billCents,
    tab.tipCents,
    tab.tipPercent,
    tab.id,
    tab.autoGratCents ?? 0,
  )
  return live.text
}

export type WeekStats = {
  weekStart: Date
  tabs: Tab[]
  billCents: number
  tipCents: number
  totalCents: number
  averageTipPercent: number
}

export function tabsInLastDays(tabs: Tab[], days: number, now = new Date()): Tab[] {
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1))
  return tabs.filter((tab) => {
    const [y, m, d] = tab.date.split('-').map(Number)
    const date = new Date(y, (m ?? 1) - 1, d ?? 1)
    return date >= cutoff
  })
}

export function summarizeTabs(tabs: Tab[]): Omit<WeekStats, 'weekStart' | 'tabs'> {
  const billCents = tabs.reduce((sum, tab) => sum + tab.billCents, 0)
  const autoGratCents = tabs.reduce((sum, tab) => sum + (tab.autoGratCents ?? 0), 0)
  const tipCents = tabs.reduce((sum, tab) => sum + tab.tipCents, 0)
  const averageTipPercent =
    billCents > 0 ? Math.round((tipCents / billCents) * 1000) / 10 : 0
  return {
    billCents,
    tipCents,
    totalCents: tabPayTotalCents(billCents, tipCents, autoGratCents),
    averageTipPercent,
  }
}

export function groupTabsByWeek(tabs: Tab[], now = new Date()): WeekStats[] {
  const groups = new Map<number, Tab[]>()
  for (const tab of tabs) {
    const [y, m, d] = tab.date.split('-').map(Number)
    const start = startOfWeek(new Date(y, (m ?? 1) - 1, d ?? 1)).getTime()
    const list = groups.get(start) ?? []
    list.push(tab)
    groups.set(start, list)
  }
  return [...groups.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([start, weekTabs]) => ({
      weekStart: new Date(start),
      tabs: weekTabs,
      ...summarizeTabs(weekTabs),
    }))
    .filter((week) => weeksAgo(week.tabs[0]?.date ?? '', now) < 8)
}

export async function roastForHabits(tabs: Tab[], now = new Date()): Promise<string> {
  if (tabs.length === 0) return pick(EMPTY_LINES, `empty:${now.toDateString()}`)

  const week = tabsInLastDays(tabs, 7, now)
  const month = tabsInLastDays(tabs, 28, now)
  const weekStats = summarizeTabs(week)
  const monthStats = summarizeTabs(month)
  const barShare =
    month.length === 0 ? 0 : month.filter((tab) => tab.kind === 'bar').length / month.length
  const cheapCount = month.filter((tab) => tab.tipPercent < CHEAP_TIP_BELOW).length
  const generousCount = month.filter((tab) => tab.tipPercent >= GENEROUS_TIP_AT).length

  if (week.length >= 3) return pick(HABIT_LINES.heavyWeek, `heavyWeek:${week.length}:${now.toDateString()}`)
  if (weekStats.totalCents >= 20000) {
    return pick(HABIT_LINES.heavySpend, `heavySpend:${weekStats.totalCents}:${now.toDateString()}`)
  }
  if (month.length >= 3 && cheapCount >= Math.ceil(month.length * 0.5)) {
    return pick(HABIT_LINES.cheapPattern, `cheapPattern:${cheapCount}:${now.toDateString()}`)
  }
  if (month.length >= 3 && generousCount >= Math.ceil(month.length * 0.5)) {
    return pick(HABIT_LINES.generousPattern, `generousPattern:${generousCount}:${now.toDateString()}`)
  }
  if (month.length >= 3 && barShare >= 0.75) {
    return pick(HABIT_LINES.barHeavy, `barHeavy:${barShare}:${now.toDateString()}`)
  }
  if (month.length > 0 && week.length === 0) {
    return pick(HABIT_LINES.quiet, `quiet:${month.length}:${now.toDateString()}`)
  }
  if (month.length > 0) {
    return `Last four weeks: ${formatMoney(monthStats.totalCents)} out, ${formatPercent(monthStats.averageTipPercent)} average tip. ${roastForTipSync(monthStats.averageTipPercent, `month:${monthStats.averageTipPercent}`)}`
  }
  return roastForTipSync(weekStats.averageTipPercent, `week:${weekStats.averageTipPercent}`)
}

export function roastEyebrow(tone: RoastTone): string {
  if (tone === 'navigator') return 'Before you drive'
  if (tone === 'cheap') return 'The cheap seat'
  if (tone === 'generous') return 'Cute, or competent?'
  if (tone === 'extra') return 'Easy, big spender'
  if (tone === 'habit') return 'Your tab so far'
  return 'Standard human'
}

export { hashRoastLine }
